import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  Stack,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as ProductIcon,
  LocalOffer as PackageIcon,
} from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { colors } from '../theme'
import {
  productsApi,
  packagesApi,
  Product,
  Package,
  CreateProductInput,
  CreatePackageInput,
} from '../services/api'
import { userAtom } from '../state/auth'
import { useNotification } from '../hooks/useNotification'
import { isAdminOrManager } from '../utils/roles'

const productTypeLabels: Record<Product['type'], string> = {
  resume: 'Resume',
  linkedin: 'LinkedIn',
  coaching: 'Coaching',
  workshop: 'Workshop',
  interview_prep: 'Interview Prep',
  other: 'Other',
}

const productTypeColors: Record<Product['type'], string> = {
  resume: colors.primary,
  linkedin: '#0077b5',
  coaching: '#10b981',
  workshop: '#8b5cf6',
  interview_prep: '#f59e0b',
  other: '#64748b',
}

const statusColors: Record<string, string> = {
  active: '#10b981',
  inactive: '#9e9e9e',
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

export default function ProductCatalog() {
  const [currentUser] = useAtom(userAtom)
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [packages, setPackages] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showSuccess, showError } = useNotification()

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ item: Product | Package; type: 'product' | 'package' } | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [newProduct, setNewProduct] = useState<CreateProductInput>({
    name: '',
    type: 'resume',
    price: 0,
    description: '',
    features: [],
  })
  const [productLoading, setProductLoading] = useState(false)
  const [featuresInput, setFeaturesInput] = useState('')

  // Package dialog
  const [packageDialogOpen, setPackageDialogOpen] = useState(false)
  const [editingPackage, setEditingPackage] = useState<Package | null>(null)
  const [newPackage, setNewPackage] = useState<CreatePackageInput>({
    name: '',
    price: 0,
    description: '',
    productIds: [],
    isPopular: false,
    discount: '',
  })
  const [packageLoading, setPackageLoading] = useState(false)

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuItem, setMenuItem] = useState<Product | Package | null>(null)
  const [menuType, setMenuType] = useState<'product' | 'package'>('product')

  const isAdmin = isAdminOrManager(currentUser)
  // isManager merged into isAdmin via isAdminOrManager helper
  const canManage = isAdmin

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [productsResult, packagesResult] = await Promise.all([
        productsApi.list({ status: 'all' }),
        packagesApi.list({ status: 'all' }),
      ])
      setProducts(Array.isArray(productsResult) ? productsResult : [])
      setPackages(Array.isArray(packagesResult) ? packagesResult : [])
    } catch (err) {
      console.error('Error loading data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load product catalog')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredPackages = packages.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Product handlers
  const handleOpenProductDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product)
      setNewProduct({
        name: product.name,
        type: product.type,
        price: product.price,
        description: product.description || '',
        features: product.features || [],
        sortOrder: product.sortOrder,
      })
      setFeaturesInput((product.features || []).join('\n'))
    } else {
      setEditingProduct(null)
      setNewProduct({
        name: '',
        type: 'resume',
        price: 0,
        description: '',
        features: [],
      })
      setFeaturesInput('')
    }
    setProductDialogOpen(true)
  }

  const handleSaveProduct = async () => {
    if (!newProduct.name) {
      showError('Product name is required')
      return
    }

    try {
      setProductLoading(true)
      const features = featuresInput.split('\n').filter((f) => f.trim())

      if (editingProduct) {
        await productsApi.update({
          id: editingProduct.id,
          ...newProduct,
          features,
        })
        showSuccess('Product updated successfully')
      } else {
        await productsApi.create({ ...newProduct, features })
        showSuccess('Product created successfully')
      }
      setProductDialogOpen(false)
      loadData()
    } catch (err) {
      console.error('Error saving product:', err)
      showError(err instanceof Error ? err.message : 'Failed to save product')
    } finally {
      setProductLoading(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      if (deleteTarget.type === 'product') {
        await productsApi.delete(deleteTarget.item.id)
        showSuccess('Product deleted successfully')
      } else {
        await packagesApi.delete(deleteTarget.item.id)
        showSuccess('Package deleted successfully')
      }
      setDeleteTarget(null)
      loadData()
    } catch (err) {
      console.error('Error deleting item:', err)
      showError(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  // Package handlers
  const handleOpenPackageDialog = (pkg?: Package) => {
    if (pkg) {
      setEditingPackage(pkg)
      setNewPackage({
        name: pkg.name,
        price: pkg.price,
        description: pkg.description || '',
        productIds: pkg.productIds || [],
        isPopular: pkg.isPopular || false,
        discount: pkg.discount || '',
        sortOrder: pkg.sortOrder,
      })
    } else {
      setEditingPackage(null)
      setNewPackage({
        name: '',
        price: 0,
        description: '',
        productIds: [],
        isPopular: false,
        discount: '',
      })
    }
    setPackageDialogOpen(true)
  }

  const handleSavePackage = async () => {
    if (!newPackage.name) {
      showError('Package name is required')
      return
    }

    try {
      setPackageLoading(true)

      if (editingPackage) {
        await packagesApi.update({
          id: editingPackage.id,
          ...newPackage,
        })
        showSuccess('Package updated successfully')
      } else {
        await packagesApi.create(newPackage)
        showSuccess('Package created successfully')
      }
      setPackageDialogOpen(false)
      loadData()
    } catch (err) {
      console.error('Error saving package:', err)
      showError(err instanceof Error ? err.message : 'Failed to save package')
    } finally {
      setPackageLoading(false)
    }
  }

  // Menu handlers
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: Product | Package,
    type: 'product' | 'package'
  ) => {
    setMenuAnchor(event.currentTarget)
    setMenuItem(item)
    setMenuType(type)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
    setMenuItem(null)
  }

  const handleOpenEdit = () => {
    if (menuType === 'product') {
      handleOpenProductDialog(menuItem as Product)
    } else {
      handleOpenPackageDialog(menuItem as Package)
    }
    handleCloseMenu()
  }

  const handleDelete = () => {
    if (!menuItem || !menuType) return
    setDeleteTarget({ item: menuItem, type: menuType })
    handleCloseMenu()
  }

  const getProductName = (productId: string) => {
    const product = products.find((p) => p.id === productId)
    return product?.name || productId
  }

  if (!canManage) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          You don't have permission to manage the product catalog
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Product Catalog</Typography>
          <PageHelpButton articleId="admin-products-manage" />
        </Stack>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
            Refresh
          </Button>
          {activeTab === 0 ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenProductDialog()}
            >
              Add Product
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPackageDialog()}
            >
              Add Package
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<ProductIcon />} iconPosition="start" label={`Products (${products.length})`} />
          <Tab icon={<PackageIcon />} iconPosition="start" label={`Packages (${packages.length})`} />
        </Tabs>

        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              placeholder={activeTab === 0 ? 'Search products...' : 'Search packages...'}
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          {activeTab === 0 ? (
            // Products Table
            filteredProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {products.length === 0 ? 'No products yet. Create one to get started.' : 'No products match your search'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{product.name}</Typography>
                            {product.description && (
                              <Typography variant="caption" color="text.secondary">
                                {product.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={productTypeLabels[product.type]}
                            size="small"
                            sx={{
                              backgroundColor: `${productTypeColors[product.type]}20`,
                              color: productTypeColors[product.type],
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(product.price)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={product.status}
                            size="small"
                            sx={{
                              backgroundColor: `${statusColors[product.status]}20`,
                              color: statusColors[product.status],
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, product, 'product')}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            // Packages Table
            filteredPackages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {packages.length === 0 ? 'No packages yet. Create one to get started.' : 'No packages match your search'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Package</TableCell>
                      <TableCell>Products</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPackages.map((pkg) => (
                      <TableRow key={pkg.id} hover>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">{pkg.name}</Typography>
                              {pkg.isPopular && (
                                <Chip label="Popular" size="small" color="primary" sx={{ height: 20 }} />
                              )}
                            </Box>
                            {pkg.description && (
                              <Typography variant="caption" color="text.secondary">
                                {pkg.description}
                              </Typography>
                            )}
                            {pkg.discount && (
                              <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                {pkg.discount}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {pkg.productIds?.map((productId) => (
                              <Chip
                                key={productId}
                                label={getProductName(productId)}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(pkg.price)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={pkg.status}
                            size="small"
                            sx={{
                              backgroundColor: `${statusColors[pkg.status]}20`,
                              color: statusColors[pkg.status],
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, pkg, 'package')}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleOpenEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newProduct.type}
                  label="Type"
                  onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value as Product['type'] })}
                >
                  {Object.entries(productTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Price"
                type="number"
                value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: Number(e.target.value) })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Box>
            <TextField
              label="Features (one per line)"
              value={featuresInput}
              onChange={(e) => setFeaturesInput(e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder="Enter each feature on a new line"
              helperText="List the key features included with this product"
            />
            <TextField
              label="Sort Order"
              type="number"
              value={newProduct.sortOrder || 0}
              onChange={(e) => setNewProduct({ ...newProduct, sortOrder: Number(e.target.value) })}
              fullWidth
              helperText="Lower numbers appear first"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProduct} variant="contained" disabled={productLoading}>
            {productLoading ? <CircularProgress size={20} /> : editingProduct ? 'Save Changes' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Package Dialog */}
      <Dialog open={packageDialogOpen} onClose={() => setPackageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPackage ? 'Edit Package' : 'Add New Package'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={newPackage.name}
              onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newPackage.description}
              onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Autocomplete
              multiple
              options={products.filter((p) => p.status === 'active')}
              getOptionLabel={(option) => option.name}
              value={products.filter((p) => newPackage.productIds?.includes(p.id))}
              onChange={(_, value) =>
                setNewPackage({ ...newPackage, productIds: value.map((p) => p.id) })
              }
              renderInput={(params) => (
                <TextField {...params} label="Included Products" placeholder="Select products" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Package Price"
                type="number"
                value={newPackage.price}
                onChange={(e) => setNewPackage({ ...newPackage, price: Number(e.target.value) })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                label="Discount Label"
                value={newPackage.discount}
                onChange={(e) => setNewPackage({ ...newPackage, discount: e.target.value })}
                fullWidth
                placeholder="e.g., Save $100 or 20% off"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Sort Order"
                type="number"
                value={newPackage.sortOrder || 0}
                onChange={(e) => setNewPackage({ ...newPackage, sortOrder: Number(e.target.value) })}
                sx={{ flex: 1 }}
                helperText="Lower numbers appear first"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newPackage.isPopular || false}
                    onChange={(e) => setNewPackage({ ...newPackage, isPopular: e.target.checked })}
                  />
                }
                label="Mark as Popular"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" disabled={packageLoading}>
            {packageLoading ? <CircularProgress size={20} /> : editingPackage ? 'Save Changes' : 'Create Package'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleteTarget?.type === 'product' ? 'Product' : 'Package'}</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete &quot;{deleteTarget?.item.name}&quot;. It cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
